import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import API from '../services/api';

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 768);

  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  const userName = localStorage.getItem('userName') || 'User';
  const isAdminRoute = location.pathname.startsWith('/admin');
  // ✅ Check if user is NEW (Never subscribed)
  const subscriptionStatus = localStorage.getItem('subscriptionStatus');
  const subscriptionPlan = localStorage.getItem('subscriptionPlan');
  const isNewUser = !subscriptionStatus || subscriptionStatus === 'none' || !subscriptionPlan;

  // ✅ New User Lock Handler
  const handleNavClick = (e, path) => {
    if (isSuperAdmin || isAdminRoute) return;

    // Agar New User hai aur Dashboard ke ilawa kisi aur tab par click kar raha hai
    if (isNewUser && path !== '/dashboard') {
      e.preventDefault();
      toast.error('Please choose a plan to access platform features!');
      navigate('/pricing');
    }
  };

  useEffect(() => {
  if (window.innerWidth <= 768) {
    setSidebarOpen(false);
  }
}, [location.pathname]);

  useEffect(() => {
  if (window.innerWidth > 768) {
    if (sidebarOpen) {
      document.body.classList.add('sidebar-expanded');
    } else {
      document.body.classList.remove('sidebar-expanded');
    }
  }
  return () => {
    document.body.classList.remove('sidebar-expanded');
  };
}, [sidebarOpen]);
  useEffect(() => {
    const handlePageClick = (e) => {
      if (sidebarOpen) {
        const sidebar = document.querySelector('.ct-sidebar');
        const hamburger = document.querySelector('.ct-hamburger');
        if (sidebar && !sidebar.contains(e.target) && hamburger && !hamburger.contains(e.target)) {
          setSidebarOpen(false);
        }
      }
    };
    document.addEventListener('click', handlePageClick);
    return () => document.removeEventListener('click', handlePageClick);
  }, [sidebarOpen]);

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // ✅ Invoice states
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetailLoading, setInvoiceDetailLoading] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const handleLogout = () => setShowLogoutModal(true);

  const confirmLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isSuperAdmin');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('subscriptionStatus');
    localStorage.removeItem('subscriptionPlan');
    localStorage.removeItem('subscriptionEndDate');
    window.location.href = '/';
  };

  const loadNotifications = async () => {
    if (!isSuperAdmin || !isAdminRoute) return;
    try {
      const response = await API.get('/admin/notifications');
      setNotifications(response.data.data || []);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Load notifications error:', error);
    }
  };

 const handleNotificationClick = async (notification) => {
    try {
      await API.patch(`/admin/notifications/${notification.id}/read`, { deleteAfterRead: false });
      
      // ✅ Check if notification is a Contact Inquiry
      if (notification.title?.includes('Contact Inquiry') || notification.link?.startsWith('inquiry_')) {
        setShowNotifications(false);
        setSelectedInquiry(notification);
      } else if (notification.link) {
        setShowNotifications(false);
        navigate(notification.link);
      }
      await loadNotifications();
    } catch (error) {
      console.error('Notification click error:', error);
    }
  };

  const clearAllNotifications = async () => {
    try {
      await API.delete('/admin/notifications/clear-all');
      setShowClearConfirm(false);
      await loadNotifications();
      toast.success('All notifications cleared');
    } catch (error) {
      toast.error('Failed to clear notifications');
    }
  };

  const markAllRead = async () => {
    try {
      await API.post('/admin/notifications/mark-all-read');
      await loadNotifications();
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications');
    }
  };

  useEffect(() => {
    if (isSuperAdmin && isAdminRoute) {
      loadNotifications();
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [isSuperAdmin, isAdminRoute]);

  // ✅ Invoice functions
  const loadInvoices = async () => {
    setInvoiceLoading(true);
    try {
      const response = await API.get('/invoices');
      setInvoices(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const openInvoiceModal = () => {
    setShowInvoiceModal(true);
    setSelectedInvoice(null);
    loadInvoices();
  };

  const openInvoiceDetail = async (invoiceId) => {
    setInvoiceDetailLoading(true);
    try {
      const response = await API.get(`/invoices/${invoiceId}`);
      setSelectedInvoice(response.data.data);
    } catch (error) {
      toast.error('Failed to load invoice details');
    } finally {
      setInvoiceDetailLoading(false);
    }
  };

  const handlePrint = () => {
    // Naya print window open karo invoice content ke saath
    const printContent = document.querySelector('.invoice-print-area');
    if (!printContent) return;

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Invoice - ${selectedInvoice?.invoice_number || ''}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 40px; background: white; }
          @page { size: A4; margin: 20mm; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
    </html>
  `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const normalNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '/icons/dashboard.png' },
    { path: '/flows', label: 'Flows', icon: '/icons/flows.png' },
    { path: '/contacts', label: 'Contacts', icon: '/icons/contacts.png' },
    { path: '/messages', label: 'Messages', icon: '/icons/messages.png' },
    { path: '/bulk-message', label: 'Bulk Message', icon: '/icons/bulk-message.png' },
{ path: '/schedule-message', label: 'Schedule', icon: '/icons/schedule.png' },
{ path: '/otp-service', label: 'OTP Service', icon: '/icons/otp.png' },
{ path: '/ai-assistants', label: 'AI Assistants', icon: '/icons/ai-assistants.png' },
    { path: '/settings', label: 'API Setup', icon: '/icons/settings.png' },
  ];
  const adminNavItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: '/icons/dashboard.png' },
    { path: '/admin/clients', label: 'Clients', icon: '/icons/clients.png' },
    { path: '/admin/subscriptions', label: 'Subscriptions', icon: '/icons/subscriptions.png' },
  ];

  const navItems = isAdminRoute ? adminNavItems : normalNavItems;

  return (
    <>
      {/* ===== PRINT STYLES ===== */}
      <style>{`
  @media print {
    /* Sab kuch hide karo */
    body * { visibility: hidden !important; }
    
    /* Sirf invoice area dikhao */
    .invoice-print-area, .invoice-print-area * { 
      visibility: visible !important; 
    }
    
    /* Invoice area ko full page pe set karo */
    .invoice-print-area {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      background: white !important;
      z-index: 999999 !important;
      padding: 40px !important;
      box-sizing: border-box !important;
    }
    
    /* Hide karo jo print mein nahi chahiye */
    .invoice-no-print { display: none !important; }
    
    /* Page margins */
    @page {
      margin: 0;
      size: A4;
    }
  }
`}</style>

      {/* ===== LOGOUT MODAL ===== */}
      {showLogoutModal && (
        <div className="logout-modal-overlay" onClick={() => setShowLogoutModal(false)}>
          <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="logout-modal-header">
              <div className="logout-modal-icon-wrap">
                <img src="/icons/logout.png" alt="Logout"
                  style={{ width: '28px', height: '28px', filter: 'brightness(0) invert(1)', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
              <div>
                <h3 className="logout-modal-title">Leaving so soon?</h3>
                <p className="logout-modal-subtitle">You will need to sign in again</p>
              </div>
            </div>
            <div className="logout-modal-body">
              <div className="logout-modal-user">
                <div className="logout-modal-avatar">{userName.charAt(0).toUpperCase()}</div>
                <div>
                  <div className="logout-modal-user-name">{userName}</div>
                  <div className="logout-modal-user-role">{isSuperAdmin ? '👑 Super Admin' : 'Admin'}</div>
                </div>
              </div>
              <p className="logout-modal-desc">Are you sure you want to log out? Your session will be ended securely.</p>
            </div>
            <div className="logout-modal-footer">
              <button className="logout-modal-btn-cancel" onClick={() => setShowLogoutModal(false)}>Stay Logged In</button>
              <button className="logout-modal-btn-confirm" onClick={confirmLogout}>Yes, Logout</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== INVOICE MODAL ===== */}
      {showInvoiceModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) { setShowInvoiceModal(false); setSelectedInvoice(null); } }}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(6px)',
            zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            className="invoice-no-print"
            style={{
              background: 'white', borderRadius: '20px',
              width: '100%', maxWidth: '860px',
              maxHeight: '90vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
              overflow: 'hidden',
              animation: 'invoiceSlideIn 0.3s ease',
            }}
          >
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #1e3a5f, #2d5986)',
              padding: '20px 28px',
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {selectedInvoice && (
                  <button
                    onClick={() => setSelectedInvoice(null)}
                    style={{
                      background: 'rgba(255,255,255,0.15)', border: 'none',
                      borderRadius: '8px', color: 'white', fontSize: '14px',
                      padding: '6px 12px', cursor: 'pointer', fontWeight: '600',
                    }}
                  >
                    ← Back
                  </button>
                )}
                <div>
                  <div style={{ color: 'white', fontSize: '18px', fontWeight: '700' }}>
                    {selectedInvoice ? `Invoice ${selectedInvoice.invoice_number}` : '🧾 My Invoices'}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginTop: '2px' }}>
                    {selectedInvoice ? 'Invoice Details' : `${invoices.length} invoice${invoices.length !== 1 ? 's' : ''} found`}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {selectedInvoice && (
                  <button
                    onClick={handlePrint}
                    style={{
                      background: 'linear-gradient(135deg, #25D366, #128C7E)',
                      border: 'none', borderRadius: '9px',
                      color: 'white', fontSize: '13px', fontWeight: '700',
                      padding: '8px 16px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '6px',
                      boxShadow: '0 3px 10px rgba(37,211,102,0.35)',
                    }}
                  >
                    🖨️ Print
                  </button>
                )}
                <button
                  onClick={() => { setShowInvoiceModal(false); setSelectedInvoice(null); }}
                  style={{
                    background: 'rgba(255,255,255,0.15)', border: 'none',
                    borderRadius: '8px', color: 'white', fontSize: '18px',
                    width: '34px', height: '34px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >✕</button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ flex: 1, overflowY: 'auto' }}>

              {/* ── INVOICE LIST VIEW ── */}
              {!selectedInvoice && (
                <div style={{ padding: '24px' }}>
                  {invoiceLoading ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                      <div style={{ fontSize: '48px', marginBottom: '12px' }}>⏳</div>
                      <p style={{ margin: 0 }}>Loading invoices...</p>
                    </div>
                  ) : invoices.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                      <div style={{ fontSize: '64px', marginBottom: '16px', opacity: 0.4 }}>🧾</div>
                      <p style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: '600', color: '#64748b' }}>No Invoices Yet</p>
                      <p style={{ margin: 0, fontSize: '14px' }}>Your invoices will appear here after you purchase a plan.</p>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            {['Invoice #', 'Plan', 'Billing Cycle', 'Amount', 'Date', 'Valid Till', 'Status', ''].map((h, i) => (
                              <th key={i} style={{
                                padding: '12px 16px', textAlign: 'left',
                                fontSize: '12px', fontWeight: '700',
                                color: '#475569', textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                borderBottom: '2px solid #e2e8f0',
                                whiteSpace: 'nowrap',
                              }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((inv, i) => (
                            <tr
                              key={inv.id}
                              style={{
                                borderBottom: '1px solid #f1f5f9',
                                transition: 'background 0.15s',
                                cursor: 'pointer',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >
                              <td style={{ padding: '14px 16px' }}>
                                <span style={{
                                  fontFamily: 'monospace', fontSize: '13px',
                                  fontWeight: '700', color: '#1e3a5f',
                                  background: '#eff6ff', padding: '3px 8px',
                                  borderRadius: '6px',
                                }}>
                                  {inv.invoice_number}
                                </span>
                              </td>
                              <td style={{ padding: '14px 16px', fontSize: '14px', color: '#1e293b', fontWeight: '600' }}>
                                {inv.plan_name}
                              </td>
                              <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748b', textTransform: 'capitalize' }}>
                                {inv.billing_cycle}
                              </td>
                              <td style={{ padding: '14px 16px' }}>
                                <span style={{ fontSize: '15px', fontWeight: '700', color: '#065f46' }}>
                                  ₹{parseFloat(inv.amount).toLocaleString('en-IN')}
                                </span>
                              </td>
                              <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap' }}>
                                {new Date(inv.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </td>
                              <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap' }}>
                                {new Date(inv.plan_end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </td>
                              <td style={{ padding: '14px 16px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{
                                    background: '#dcfce7', color: '#166534',
                                    padding: '4px 10px', borderRadius: '20px',
                                    fontSize: '12px', fontWeight: '700',
                                  }}>
                                    ✓ Paid
                                  </span>
                                  {inv.subscription_status && inv.subscription_status !== 'active' && (
                                    <span style={{
                                      background: inv.subscription_status === 'deleted_by_admin' ? '#fee2e2'
                                        : inv.subscription_status === 'expired' ? '#fef3c7'
                                          : '#f1f5f9',
                                      color: inv.subscription_status === 'deleted_by_admin' ? '#991b1b'
                                        : inv.subscription_status === 'expired' ? '#92400e'
                                          : '#475569',
                                      padding: '3px 8px', borderRadius: '20px',
                                      fontSize: '11px', fontWeight: '600',
                                    }}>
                                      {inv.subscription_status === 'deleted_by_admin' ? '🗑 Removed'
                                        : inv.subscription_status === 'expired' ? '⏰ Expired'
                                          : inv.subscription_status}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td style={{ padding: '14px 16px' }}>
                                <button
                                  onClick={() => openInvoiceDetail(inv.id)}
                                  style={{
                                    background: '#1e3a5f', color: 'white',
                                    border: 'none', borderRadius: '7px',
                                    padding: '6px 14px', fontSize: '12px',
                                    fontWeight: '600', cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  View →
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ── INVOICE DETAIL VIEW ── */}
              {selectedInvoice && (
                invoiceDetailLoading ? (
                  <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>⏳</div>
                    <p style={{ margin: 0 }}>Loading details...</p>
                  </div>
                ) : (
                  <div className="invoice-print-area" style={{ padding: '32px 36px' }}>

                    {/* Invoice Top — Company + Invoice Info */}
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start', marginBottom: '32px',
                      flexWrap: 'wrap', gap: '20px',
                    }}>
                      {/* Left — Company */}
                      <div>
                        <img
                          src="/logo.png"
                          alt="ChatTech24"
                          style={{ height: '50px', objectFit: 'contain', marginBottom: '8px', display: 'block' }}
                        />
                        <div style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.6' }}>
                          by Vedvika Technologies<br />
                          support@chattech24.com<br />
                          chattech24.com
                        </div>
                      </div>

                      {/* Right — Invoice Badge */}
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          background: 'linear-gradient(135deg, #1e3a5f, #2d5986)',
                          color: 'white', padding: '8px 20px',
                          borderRadius: '10px', marginBottom: '10px',
                          display: 'inline-block',
                        }}>
                          <div style={{ fontSize: '11px', opacity: 0.8, letterSpacing: '1px' }}>INVOICE</div>
                          <div style={{ fontSize: '18px', fontWeight: '800', fontFamily: 'monospace' }}>
                            {selectedInvoice.invoice_number}
                          </div>
                        </div>
                        <div style={{ fontSize: '13px', color: '#64748b' }}>
                          <div>Date: <strong>{new Date(selectedInvoice.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</strong></div>
                          <div style={{ marginTop: '4px' }}>
                            Status: <span style={{
                              background: '#dcfce7', color: '#166534',
                              padding: '2px 8px', borderRadius: '4px',
                              fontWeight: '700', fontSize: '12px',
                            }}>✓ PAID</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div style={{ height: '2px', background: 'linear-gradient(90deg, #1e3a5f, #25D366)', marginBottom: '28px', borderRadius: '2px' }} />

                    {/* Bill To + Plan Period */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '28px' }}>
                      {/* Bill To */}
                      <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '18px 20px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', letterSpacing: '1px', marginBottom: '10px' }}>
                          BILL TO
                        </div>
                        <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
                          {selectedInvoice.billing_name || selectedInvoice.org_name || '—'}
                        </div>
                        {selectedInvoice.billing_email && (
                          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '2px' }}>
                            {selectedInvoice.billing_email}
                          </div>
                        )}
                        {selectedInvoice.billing_phone && (
                          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '2px' }}>
                            {selectedInvoice.billing_phone}
                          </div>
                        )}
                        {selectedInvoice.billing_address && (
                          <div style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.6', marginTop: '6px' }}>
                            {selectedInvoice.billing_address}<br />
                            {[selectedInvoice.billing_city, selectedInvoice.billing_state, selectedInvoice.billing_pincode].filter(Boolean).join(', ')}<br />
                            {selectedInvoice.billing_country}
                          </div>
                        )}
                        {!selectedInvoice.billing_address && (
                          <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '4px' }}>
                            {selectedInvoice.org_name}
                          </div>
                        )}
                      </div>

                      {/* Plan Period */}
                      <div style={{ background: '#f0fdf9', borderRadius: '12px', padding: '18px 20px', border: '1px solid rgba(37,211,102,0.2)' }}>
                        <div style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', letterSpacing: '1px', marginBottom: '10px' }}>
                          SUBSCRIPTION PERIOD
                        </div>
                        <div style={{ fontSize: '13px', color: '#475569', marginBottom: '8px' }}>
                          <span style={{ fontWeight: '600', color: '#1e293b' }}>Start: </span>
                          {new Date(selectedInvoice.plan_start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </div>
                        <div style={{ fontSize: '13px', color: '#475569', marginBottom: '8px' }}>
                          <span style={{ fontWeight: '600', color: '#1e293b' }}>End: </span>
                          {new Date(selectedInvoice.plan_end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </div>
                        <div style={{ fontSize: '13px', color: '#475569' }}>
                          <span style={{ fontWeight: '600', color: '#1e293b' }}>Billing: </span>
                          <span style={{ textTransform: 'capitalize' }}>{selectedInvoice.billing_cycle}</span>
                        </div>
                      </div>
                    </div>

                    {/* Plan Table */}
                    <div style={{ marginBottom: '24px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '12px', overflow: 'hidden' }}>
                        <thead>
                          <tr style={{ background: 'linear-gradient(135deg, #1e3a5f, #2d5986)' }}>
                            {['Description', 'Billing Cycle', 'Currency', 'Amount'].map((h, i) => (
                              <th key={i} style={{
                                padding: '14px 18px', textAlign: i === 3 ? 'right' : 'left',
                                fontSize: '12px', fontWeight: '700',
                                color: 'rgba(255,255,255,0.9)',
                                letterSpacing: '0.5px',
                              }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '16px 18px' }}>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>{selectedInvoice.plan_name}</div>
                              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>ChatTech24 Subscription Plan</div>
                            </td>
                            <td style={{ padding: '16px 18px', fontSize: '14px', color: '#475569', textTransform: 'capitalize' }}>
                              {selectedInvoice.billing_cycle}
                            </td>
                            <td style={{ padding: '16px 18px', fontSize: '14px', color: '#475569' }}>
                              {selectedInvoice.currency}
                            </td>
                            <td style={{ padding: '16px 18px', textAlign: 'right', fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>
                              ₹{parseFloat(selectedInvoice.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#f0fdf9' }}>
                            <td colSpan={3} style={{ padding: '16px 18px', fontSize: '15px', fontWeight: '700', color: '#065f46', textAlign: 'right' }}>
                              Total Amount Paid
                            </td>
                            <td style={{ padding: '16px 18px', textAlign: 'right' }}>
                              <div style={{ fontSize: '22px', fontWeight: '800', color: '#128C7E' }}>
                                ₹{parseFloat(selectedInvoice.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </div>
                              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Inclusive of all taxes</div>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* ✅ Subscription removed notice */}
                    {selectedInvoice.subscription_status && selectedInvoice.subscription_status !== 'active' && (
                      <div style={{
                        background: '#fee2e2', borderRadius: '10px',
                        padding: '14px 20px', marginBottom: '16px',
                        border: '1px solid #fecaca',
                        display: 'flex', alignItems: 'center', gap: '10px',
                      }}>
                        <span style={{ fontSize: '20px' }}>⚠️</span>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#991b1b' }}>
                            {selectedInvoice.subscription_status === 'deleted_by_admin'
                              ? 'Subscription Removed by Admin'
                              : selectedInvoice.subscription_status}
                          </div>
                          <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: '2px' }}>
                            This subscription has been cancelled. Invoice record is permanently saved.
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Footer Note */}
                    <div style={{
                      background: '#f8fafc', borderRadius: '10px',
                      padding: '16px 20px', border: '1px solid #e2e8f0',
                      fontSize: '12px', color: '#64748b', lineHeight: '1.6',
                      textAlign: 'center',
                    }}>
                      Thank you for choosing ChatTech24! For any queries regarding this invoice, please contact{' '}
                      <strong>legal@chattech24.com</strong>
                      <br />
                      This is a computer-generated invoice and does not require a physical signature.
                    </div>

                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
  {/* ===== CONTACT INQUIRY POPUP MODAL (SUPER ADMIN) ===== */}
      {selectedInquiry && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setSelectedInquiry(null); }}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(6px)',
            zIndex: 999999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            style={{
              background: 'white', borderRadius: '20px',
              width: '100%', maxWidth: '520px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px rgba(0,0,0,0.35)',
              animation: 'slideIn 0.25s ease-out',
            }}
          >
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #075E54, #128C7E)',
              padding: '20px 24px',
              display: 'flex', alignItems: 'center',
              justify_content: 'space-between', color: 'white',
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>📩 Contact Inquiry Details</h3>
                <p style={{ margin: '4px 0 0', fontSize: '12px', opacity: 0.85 }}>Submitted via "Get in Touch" form</p>
              </div>
              <button
                onClick={() => setSelectedInquiry(null)}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none',
                  color: 'white', borderRadius: '8px',
                  width: '32px', height: '32px', cursor: 'pointer',
                  fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>

            {/* Details Body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', letterSpacing: '0.5px', display: 'block', marginBottom: '2px' }}>NAME</span>
                <span style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>
                  {selectedInquiry.message.split(' | ')[0]?.replace('Name: ', '') || '—'}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', letterSpacing: '0.5px', display: 'block', marginBottom: '2px' }}>EMAIL</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#0284c7', wordBreak: 'break-all' }}>
                    {selectedInquiry.message.split(' | ')[1]?.replace('Email: ', '') || '—'}
                  </span>
                </div>
                <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', letterSpacing: '0.5px', display: 'block', marginBottom: '2px' }}>PHONE</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>
                    {selectedInquiry.message.split(' | ')[2]?.replace('Phone: ', '') || '—'}
                  </span>
                </div>
              </div>

              <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', letterSpacing: '0.5px', display: 'block', marginBottom: '2px' }}>USE CASE</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#059669' }}>
                  {selectedInquiry.message.split(' | ')[3]?.replace('Use Case: ', '') || '—'}
                </span>
              </div>

              <div style={{ background: '#f0fdf4', padding: '14px 16px', borderRadius: '10px', border: '1px solid #bbf7d0' }}>
                <span style={{ fontSize: '11px', fontWeight: '800', color: '#166534', letterSpacing: '0.5px', display: 'block', marginBottom: '4px' }}>MESSAGE</span>
                <p style={{ margin: 0, fontSize: '13px', color: '#14532d', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                  {selectedInquiry.message.split(' | ')[4]?.replace('Message: ', '') || selectedInquiry.message}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSelectedInquiry(null)}
                style={{
                  padding: '9px 20px', background: '#128C7E',
                  color: 'white', border: 'none', borderRadius: '9px',
                  fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                }}
              >
                Close Details
              </button>
            </div>

          </div>
        </div>
      )}
      {/* Mobile overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* ===== SIDEBAR ===== */}
      <div className={`ct-sidebar ${sidebarOpen ? 'ct-sidebar-open' : ''}`}>
        <div className="ct-sidebar-header">
          <img src="/logo 1.png" alt="ChatTech24"
            style={{ width: '34px', height: '34px', objectFit: 'contain', flexShrink: 0 }} />
          {sidebarOpen && (
            <span className="ct-sidebar-brand" style={{
              color: '#e7e7e7', fontFamily: 'Cooper Black, serif',
              fontSize: '13px', marginLeft: '-10px',
            }}>ChatTech24</span>
          )}
        </div>

        <ul className="ct-sidebar-nav">
          {navItems.map((item) => (
            <li key={item.path} className="ct-sidebar-nav-item">
              <Link
                to={item.path}
                onClick={(e) => handleNavClick(e, item.path)}
                className={`ct-sidebar-link ${isActive(item.path) ? 'ct-sidebar-link-active' : ''}`}
              >                <i className="ct-sidebar-icon">
                  {item.icon.startsWith('/') ? (
                    <img src={item.icon} alt={item.label} className="ct-sidebar-icon-img"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : item.icon}
                </i>
                <span className="ct-sidebar-label">{item.label}</span>
                {!sidebarOpen && <span className="ct-tooltip">{item.label}</span>}
              </Link>
            </li>
          ))}

          {isSuperAdmin && (
            <li className="ct-sidebar-nav-item ct-sidebar-divider-item">
              {isAdminRoute ? (
                <button onClick={() => navigate('/dashboard')} className="ct-sidebar-link ct-sidebar-link-btn">
                  <i className="ct-sidebar-icon">←</i>
                  <span className="ct-sidebar-label">My Dashboard</span>
                  {!sidebarOpen && <span className="ct-tooltip">My Dashboard</span>}
                </button>
              ) : (
                <button onClick={() => navigate('/admin/dashboard')} className="ct-sidebar-link ct-sidebar-link-btn ct-sidebar-link-admin">
                  <i className="ct-sidebar-icon">👑</i>
                  <span className="ct-sidebar-label">Admin Panel</span>
                  {!sidebarOpen && <span className="ct-tooltip">Admin Panel</span>}
                </button>
              )}
            </li>
          )}
        </ul>

        <div className="ct-sidebar-footer">
          <button onClick={handleLogout} className="ct-sidebar-link ct-sidebar-link-btn ct-sidebar-link-logout">
            <i className="ct-sidebar-icon">
              <img src="/icons/logout.png" alt="Logout" className="ct-sidebar-icon-img"
                onError={(e) => { e.target.style.display = 'none'; }} />
            </i>
            <span className="ct-sidebar-label">Logout</span>
            {!sidebarOpen && <span className="ct-tooltip">Logout</span>}
          </button>
        </div>
      </div>

      {/* ===== TOP NAVBAR ===== */}
      <nav className={`ct-navbar ${sidebarOpen ? 'ct-navbar-shifted' : ''}`}>
        <div className="ct-navbar-left">
          <button className="ct-hamburger" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle sidebar">
            <span className={`ct-hamburger-icon ${sidebarOpen ? 'ct-hamburger-open' : ''}`}>
              {sidebarOpen ? '✕' : '☰'}
            </span>
          </button>
          <span className="ct-navbar-brand-mobile">ChatTech24</span>
          {isAdminRoute && <span className="ct-navbar-admin-badge">👑 Super Admin</span>}
        </div>

        <div className="ct-navbar-right">

          {/* ✅ NOTIFICATION BELL - Super Admin Only */}
          {isSuperAdmin && isAdminRoute && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="ct-navbar-icon-btn" title="Notifications"
                style={{ position: 'relative' }}
              >
                <span style={{ fontSize: '20px' }}>🔔</span>
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: '6px', right: '6px',
                    background: '#ef4444', color: 'white',
                    fontSize: '11px', fontWeight: '700',
                    padding: '2px 6px', borderRadius: '10px',
                    minWidth: '18px', textAlign: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}>{unreadCount}</span>
                )}
                <span className="ct-navbar-icon-label">Notifications</span>
              </button>

              {showNotifications && (
                <>
                  <div onClick={() => setShowNotifications(false)}
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998, background: 'rgba(0,0,0,0.3)' }} />
                  <div style={{
                    position: 'fixed',
                    top: window.innerWidth <= 768 ? '70px' : '60px',
                    right: window.innerWidth <= 768 ? '10px' : '20px',
                    left: window.innerWidth <= 768 ? '10px' : 'auto',
                    width: window.innerWidth <= 768 ? 'calc(100% - 20px)' : '400px',
                    maxHeight: window.innerWidth <= 768 ? 'calc(100vh - 140px)' : '500px',
                    background: 'white', borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                    zIndex: 999, overflow: 'hidden',
                    border: '1px solid #e5e7eb',
                    animation: 'slideIn 0.2s ease-out',
                  }}>
                    <div style={{
                      padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      background: '#f9fafb', position: 'sticky', top: 0, zIndex: 1,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>🔔 Notifications</h3>
                        {unreadCount > 0 && (
                          <span style={{ background: '#ef4444', color: 'white', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px' }}>
                            {unreadCount}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {unreadCount > 0 && (
                          <button onClick={markAllRead} style={{ padding: '6px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                            ✓ Mark all
                          </button>
                        )}
                        {notifications.length > 0 && (
                          <button onClick={() => setShowClearConfirm(true)} style={{ padding: '6px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                            🗑️ Clear All
                          </button>
                        )}
                      </div>
                    </div>

                    <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                      {notifications.length === 0 ? (
                        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af' }}>
                          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔕</div>
                          <p style={{ margin: 0, fontSize: '14px' }}>No notifications</p>
                        </div>
                      ) : (
                        notifications.map(notif => (
                          <div key={notif.id} onClick={() => handleNotificationClick(notif)}
                            style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: notif.is_read ? 'white' : '#eff6ff', transition: 'background 0.2s' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                            onMouseLeave={(e) => e.currentTarget.style.background = notif.is_read ? 'white' : '#eff6ff'}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', gap: '8px' }}>
                              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1f2937', flex: 1, lineHeight: '1.4' }}>{notif.title}</h4>
                              {!notif.is_read && <span style={{ width: '8px', height: '8px', background: '#3b82f6', borderRadius: '50%', flexShrink: 0, marginTop: '4px' }} />}
                            </div>
                            <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#6b7280', lineHeight: '1.4' }}>{notif.message}</p>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                              <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                                {new Date(notif.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                              {notif.organization_name && (
                                <span style={{ fontSize: '11px', background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px', color: '#6b7280', fontWeight: '600' }}>
                                  {notif.organization_name}
                                </span>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {showClearConfirm && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}>
                      <div style={{ background: 'white', borderRadius: '16px', maxWidth: '400px', width: '90%', overflow: 'hidden', boxShadow: '0 25px 50px rgba(0,0,0,0.35)' }}>
                        <div style={{ background: '#ef4444', padding: '18px 24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '38px', height: '38px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>🗑️</div>
                          <div>
                            <h3 style={{ margin: 0, color: 'white', fontSize: '16px', fontWeight: '700' }}>Clear All Notifications</h3>
                            <p style={{ margin: 0, color: 'rgba(255,255,255,0.85)', fontSize: '12px' }}>This action cannot be undone</p>
                          </div>
                        </div>
                        <div style={{ padding: '20px 24px' }}>
                          <p style={{ color: '#374151', fontSize: '14px', marginBottom: '20px', lineHeight: '1.6' }}>
                            Are you sure you want to permanently delete <strong>all notifications</strong>?
                          </p>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => setShowClearConfirm(false)} style={{ flex: 1, padding: '11px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={clearAllNotifications} style={{ flex: 1, padding: '11px', background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>🗑️ Yes, Clear All</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <style>{`@keyframes slideIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
                </>
              )}
            </div>
          )}

          {/* ✅ INVOICE BUTTON — Normal users only (non-admin route) */}
          {!isAdminRoute && (
            <button
              onClick={(e) => {
                if (!isSuperAdmin && isNewUser) {
                  toast.error('Please choose a plan to access platform features!');
                  navigate('/pricing');
                } else {
                  openInvoiceModal();
                }
              }}
              className="ct-navbar-icon-btn"
              title="My Invoices"
            >
              <span style={{ fontSize: '20px' }}>🧾</span>
              <span className="ct-navbar-icon-label">Invoices</span>
            </button>
          )}

          {/* ✅ ORGANIZATION BUTTON - Protected */}
          <button 
            onClick={(e) => {
              if (!isSuperAdmin && isNewUser) {
                toast.error('Please choose a plan to access platform features!');
                navigate('/pricing');
              } else {
                navigate('/organization');
              }
            }} 
            className="ct-navbar-icon-btn" 
            title="Organization"
          >
            <span>🏢</span>
            <span className="ct-navbar-icon-label">Organization</span>
          </button>

          {/* ✅ STATISTICS BUTTON - Protected */}
          <button 
            onClick={(e) => {
              if (!isSuperAdmin && isNewUser) {
                toast.error('Please choose a plan to access platform features!');
                navigate('/pricing');
              } else {
                navigate('/statistics');
              }
            }} 
            className="ct-navbar-icon-btn" 
            title="Statistics"
          >
            <span>📈</span>
            <span className="ct-navbar-icon-label">Statistics</span>
          </button>

          {/* User badge */}
          <div className="ct-user-badge">
            <div className={`ct-user-avatar ${isSuperAdmin ? 'ct-user-avatar-admin' : ''}`}>
              {userName.charAt(0).toUpperCase()}
            </div>
            <div className="ct-user-info">
              <div className="ct-user-name">{userName}</div>
              <div className={`ct-user-role ${isSuperAdmin ? 'ct-user-role-admin' : ''}`}>
                {isSuperAdmin ? '👑 Super Admin' : 'Admin'}
              </div>
            </div>
          </div>

        </div>
      </nav>
    </>
  );
}

export default Navbar;